import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { SUN_ATTACK_ETA_INTELLIGENCE_LEVEL } from '@kaeliux/shared';
import { getSunAlerts } from '../services/sun.service';
import { useNow } from '../hooks/useNow';
import { formatCountdown } from '../utils/formatters';
import { Icon } from './ui/Icon';
import { Panel } from './ui/Panel';

/**
 * Inbound Sun attacks this player is told about (`buildSunAttackAlerts`): an
 * attack on an uncontrolled Sun near one of their planets, or a reclaim of
 * a Sun their alliance holds. The arrival time needs Intelligence at Level
 * 10; below that the alert only says an attack is coming. Hidden when there
 * is nothing to report.
 */
export default function SunAttackAlertsPanel() {
    const now = useNow();
    const { data: alerts = [] } = useQuery({
        queryKey: ['sun', 'alerts'],
        queryFn: getSunAlerts,
        refetchInterval: 60_000,
    });
    if (alerts.length === 0) return null;

    return (
        <Panel variant="solid" className="flex flex-col gap-2 border-status-danger/40" data-testid="sun-attack-alerts">
            <h2 className="text-title text-content-primary font-semibold flex items-center gap-2">
                <Icon name="alert" size={18} className="text-status-danger" />
                Sun attacks
            </h2>
            {alerts.map((alert) => {
                const coords = `${alert.target.galaxy}:${alert.target.system}`;
                return (
                    <div key={alert.sunId} className="flex flex-wrap items-center justify-between gap-2" data-testid={`sun-attack-alert-${alert.sunId}`}>
                        <p className="text-body text-content-primary">
                            {alert.kind === 'reclaim'
                                ? <>A reclaim is inbound to your alliance&apos;s Sun at <span className="font-mono">{coords}</span>.</>
                                : <>An attack is inbound to the Sun at <span className="font-mono">{coords}</span>.</>}{' '}
                            {alert.arrivesAt ? (
                                <span className="text-status-danger font-semibold" data-testid="sun-attack-alert-eta">
                                    Arrives in {formatCountdown(Math.max(0, alert.arrivesAt.getTime() - now))}.
                                </span>
                            ) : (
                                <span className="text-content-muted" data-testid="sun-attack-alert-no-eta">
                                    Research Intelligence to Level {SUN_ATTACK_ETA_INTELLIGENCE_LEVEL} to see when it arrives.
                                </span>
                            )}
                        </p>
                        <Link to={`/galaxy/${alert.target.galaxy}/${alert.target.system}`} className="text-label text-content-secondary hover:underline">
                            View Sun
                        </Link>
                    </div>
                );
            })}
        </Panel>
    );
}
